import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiService } from '@/services/api';
import { useToast } from '@/hooks/use-toast';
import { Button } from '@/components/ui/button';
import { useConfirm } from '@/components/ConfirmDialog';
import { 
  Receipt, Plus, Search, Download, Eye, Trash2, Send, CheckCircle2, 
  Clock, AlertCircle, XCircle, Grid3x3, List,
  Calendar, User, Filter, FileSpreadsheet, Ban, ArrowRight, ArrowLeft
} from 'lucide-react';
import { CreateInvoiceModal } from '@/components/invoices/CreateInvoiceModal';
import { InitNextInvoiceModal } from '@/components/invoices/InitNextInvoiceModal';
import { CancelInvoiceModal } from '@/components/invoices/CancelInvoiceModal';

type Invoice = {
  id: number;
  invoice_number: string;
  quote_id: number;
  percentage: number;
  payment_label?: string;
  subtotal: number;
  tax_amount: number;
  total: number;
  customer_name?: string;
  customer_email?: string;
  customer_address?: string;
  customer_city?: string;
  customer_postal_code?: string;
  status: 'draft' | 'sent' | 'paid' | 'overdue' | 'cancelled';
  issue_date: string;
  due_date?: string;
  paid_date?: string;
  notes?: string;
  payment_terms?: string;
  cancellation_reason?: string;
  created_at: string;
};

type ViewMode = 'grid' | 'list';
type StatusFilter = 'all' | 'draft' | 'sent' | 'paid' | 'overdue' | 'cancelled';

const InvoiceList: React.FC = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { showConfirm } = useConfirm();
  const [items, setItems] = useState<Invoice[]>([]);
  const [busy, setBusy] = useState(false);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showInitNextModal, setShowInitNextModal] = useState(false);
  const [selectedInvoiceForNext, setSelectedInvoiceForNext] = useState<Invoice | null>(null);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [selectedInvoiceForCancel, setSelectedInvoiceForCancel] = useState<Invoice | null>(null);

  const load = async () => {
    try {
      setBusy(true);
      const params: any = { limit: 200 };
      if (statusFilter !== 'all') params.status = statusFilter;
      if (search) params.search = search;
      
      const list = await apiService.listInvoices(params);
      setItems(Array.isArray(list) ? list : []);
    } catch (e: any) {
      toast({ title: 'Erreur', description: 'Impossible de charger les factures.', variant: 'destructive' });
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    void load();
  }, [statusFilter]);

  useEffect(() => {
    const timer = setTimeout(() => void load(), 300);
    return () => clearTimeout(timer);
  }, [search]);

  const handleDelete = async (invoice: Invoice) => {
    const confirmed = await showConfirm({
      title: 'Supprimer la facture',
      message: `Êtes-vous sûr de vouloir supprimer la facture ${invoice.invoice_number} ? Cette action est irréversible.`,
      confirmText: 'Supprimer',
      cancelText: 'Annuler',
      severity: 'error'
    });
    
    if (!confirmed) return;
    
    try {
      await apiService.deleteInvoice(invoice.id);
      toast({ title: 'Facture supprimée', description: 'La facture a été supprimée avec succès.' });
      void load();
    } catch (e: any) {
      toast({ title: 'Erreur', description: 'Suppression impossible.', variant: 'destructive' });
    }
  };

  const handleSend = async (invoice: Invoice) => {
    try {
      await apiService.sendInvoice(invoice.id);
      toast({ title: 'Facture envoyée', description: 'La facture a été marquée comme envoyée.' });
      void load();
    } catch (e: any) {
      toast({ title: 'Erreur', description: 'Envoi impossible.', variant: 'destructive' });
    }
  };

  const handleMarkPaid = async (invoice: Invoice) => {
    try {
      await apiService.markInvoicePaid(invoice.id);
      toast({ title: 'Facture payée', description: 'La facture a été marquée comme payée.' });
      void load();
    } catch (e: any) {
      toast({ title: 'Erreur', description: 'Mise à jour impossible.', variant: 'destructive' });
    }
  };

  const handleCancel = (invoice: Invoice) => {
    setSelectedInvoiceForCancel(invoice);
    setShowCancelModal(true);
  };

  const handleInitNext = (invoice: Invoice) => {
    setSelectedInvoiceForNext(invoice);
    setShowInitNextModal(true);
  };

  const handleDownload = async (invoice: Invoice) => {
    try {
      const { blob, filename } = await apiService.getInvoicePdfBlob(invoice.id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e: any) {
      toast({ title: 'Erreur', description: 'Téléchargement impossible.', variant: 'destructive' });
    }
  };

  const handleExportCSV = () => {
    const headers = ['Numéro', 'Client', 'Montant TTC', 'Statut', 'Date émission', 'Échéance', 'Pourcentage'];
    const rows = items.map(inv => [
      inv.invoice_number,
      inv.customer_name || '',
      inv.total.toFixed(2),
      inv.status,
      new Date(inv.issue_date).toLocaleDateString('fr-FR'),
      inv.due_date ? new Date(inv.due_date).toLocaleDateString('fr-FR') : '',
      `${inv.percentage}%`
    ]);
    
    const csv = [headers, ...rows].map(row => row.map(cell => `"${cell}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `factures-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const fmtDate = (dateStr?: string) => {
    if (!dateStr) return '—';
    try {
      return new Date(dateStr).toLocaleDateString('fr-FR', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      });
    } catch {
      return dateStr;
    }
  };

  const getStatusConfig = (status: string) => {
    const configs = {
      draft: { label: 'Brouillon', color: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300', icon: Clock },
      sent: { label: 'Envoyée', color: 'bg-blue-100 text-blue-700 dark:bg-blue-950/30 dark:text-blue-300', icon: Send },
      paid: { label: 'Payée', color: 'bg-green-100 text-green-700 dark:bg-green-950/30 dark:text-green-300', icon: CheckCircle2 },
      overdue: { label: 'En retard', color: 'bg-red-100 text-red-700 dark:bg-red-950/30 dark:text-red-300', icon: AlertCircle },
      cancelled: { label: 'Annulée', color: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300', icon: XCircle },
    };
    return configs[status as keyof typeof configs] || configs.draft;
  };

  const filteredItems = items;

  const stats = {
    total: items.length,
    draft: items.filter(i => i.status === 'draft').length,
    sent: items.filter(i => i.status === 'sent').length,
    paid: items.filter(i => i.status === 'paid').length,
    overdue: items.filter(i => i.status === 'overdue').length,
    totalAmount: items.reduce((sum, i) => sum + i.total, 0),
    paidAmount: items.filter(i => i.status === 'paid').reduce((sum, i) => sum + i.total, 0),
  };

  return (
    <div className="h-screen flex flex-col bg-gradient-to-br from-slate-50 via-amber-50/20 to-slate-50 dark:from-slate-950 dark:via-amber-950/10 dark:to-slate-950">
      {/* Back Button - Minimal but Visible */}
      <div className="px-6 pt-4 pb-3 bg-white/50 dark:bg-slate-900/50 border-b border-slate-200 dark:border-slate-700">
        <button
          onClick={() => navigate('/achat/orders')}
          className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold text-cyan-700 dark:text-cyan-300 bg-cyan-50 dark:bg-cyan-950/40 border border-cyan-200 dark:border-cyan-800 hover:bg-cyan-100 dark:hover:bg-cyan-950/60 hover:border-cyan-300 dark:hover:border-cyan-700 rounded-lg transition-all group shadow-sm"
        >
          <ArrowLeft className="h-4 w-4 group-hover:-translate-x-1 transition-transform" />
          <span>Retour aux Commandes</span>
        </button>
      </div>

      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 bg-white/80 dark:bg-slate-900/80 backdrop-blur-xl border-b border-slate-200/50 dark:border-slate-700/50 shadow-lg">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 bg-gradient-to-br from-amber-500 via-orange-500 to-amber-600 rounded-xl flex items-center justify-center shadow-lg">
            <Receipt className="h-6 w-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100">Factures</h1>
            <p className="text-sm text-slate-500 dark:text-slate-400">
              {stats.total} facture{stats.total > 1 ? 's' : ''} • {stats.totalAmount.toFixed(2)} € total
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handleExportCSV}
            className="hover:bg-green-50 hover:text-green-700 dark:hover:bg-green-950/30"
          >
            <FileSpreadsheet className="h-4 w-4 mr-2" />
            Export CSV
          </Button>
          <Button
            onClick={() => setShowCreateModal(true)}
            className="bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 text-white shadow-lg"
          >
            <Plus className="h-4 w-4 mr-2" />
            Nouvelle Facture
          </Button>
        </div>
      </div>

      {/* Stats Cards - Cliquables pour filtrer */}
      <div className="px-6 py-4 grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
        <button
          onClick={() => setStatusFilter('draft')}
          className="bg-white dark:bg-slate-900 rounded-xl p-4 border border-slate-200 dark:border-slate-700 shadow-sm hover:shadow-md hover:border-slate-400 dark:hover:border-slate-500 transition-all text-left"
        >
          <div className="text-xs text-slate-500 dark:text-slate-400 mb-1">Brouillons</div>
          <div className="text-2xl font-bold text-slate-700 dark:text-slate-300">{stats.draft}</div>
        </button>
        <button
          onClick={() => setStatusFilter('sent')}
          className="bg-white dark:bg-slate-900 rounded-xl p-4 border border-slate-200 dark:border-slate-700 shadow-sm hover:shadow-md hover:border-blue-400 dark:hover:border-blue-500 transition-all text-left"
        >
          <div className="text-xs text-slate-500 dark:text-slate-400 mb-1">Envoyées</div>
          <div className="text-2xl font-bold text-blue-600">{stats.sent}</div>
        </button>
        <button
          onClick={() => setStatusFilter('paid')}
          className="bg-white dark:bg-slate-900 rounded-xl p-4 border border-slate-200 dark:border-slate-700 shadow-sm hover:shadow-md hover:border-green-400 dark:hover:border-green-500 transition-all text-left"
        >
          <div className="text-xs text-slate-500 dark:text-slate-400 mb-1">Payées</div>
          <div className="text-2xl font-bold text-green-600">{stats.paid}</div>
        </button>
        <button
          onClick={() => setStatusFilter('overdue')}
          className="bg-white dark:bg-slate-900 rounded-xl p-4 border border-slate-200 dark:border-slate-700 shadow-sm hover:shadow-md hover:border-red-400 dark:hover:border-red-500 transition-all text-left"
        >
          <div className="text-xs text-slate-500 dark:text-slate-400 mb-1">En retard</div>
          <div className="text-2xl font-bold text-red-600">{stats.overdue}</div>
        </button>
        <button
          onClick={() => setStatusFilter('all')}
          className="bg-white dark:bg-slate-900 rounded-xl p-4 border border-slate-200 dark:border-slate-700 shadow-sm hover:shadow-md hover:border-amber-400 dark:hover:border-amber-500 transition-all text-left"
        >
          <div className="text-xs text-slate-500 dark:text-slate-400 mb-1">Total TTC</div>
          <div className="text-lg font-bold text-amber-600">{stats.totalAmount.toFixed(2)} €</div>
        </button>
        <button
          onClick={() => setStatusFilter('paid')}
          className="bg-white dark:bg-slate-900 rounded-xl p-4 border border-slate-200 dark:border-slate-700 shadow-sm hover:shadow-md hover:border-green-400 dark:hover:border-green-500 transition-all text-left"
        >
          <div className="text-xs text-slate-500 dark:text-slate-400 mb-1">Encaissé</div>
          <div className="text-lg font-bold text-green-600">{stats.paidAmount.toFixed(2)} €</div>
        </button>
      </div>

      {/* Filters & Search */}
      <div className="px-6 py-3 flex items-center gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Rechercher par numéro, client, email..."
            className="w-full h-10 pl-10 pr-4 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500"
          />
        </div>
        
        <div className="flex items-center gap-2">
          <Filter className="h-4 w-4 text-slate-500" />
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
            className="h-10 px-3 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500"
          >
            <option value="all">Tous les statuts</option>
            <option value="draft">Brouillons</option>
            <option value="sent">Envoyées</option>
            <option value="paid">Payées</option>
            <option value="overdue">En retard</option>
            <option value="cancelled">Annulées</option>
          </select>
        </div>

        <div className="flex items-center gap-1 border border-slate-300 dark:border-slate-600 rounded-lg p-1">
          <button
            onClick={() => setViewMode('grid')}
            className={`p-2 rounded ${viewMode === 'grid' ? 'bg-amber-100 dark:bg-amber-950/30 text-amber-700' : 'text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800'}`}
          >
            <Grid3x3 className="h-4 w-4" />
          </button>
          <button
            onClick={() => setViewMode('list')}
            className={`p-2 rounded ${viewMode === 'list' ? 'bg-amber-100 dark:bg-amber-950/30 text-amber-700' : 'text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800'}`}
          >
            <List className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto px-6 pb-6">
        {busy ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-600"></div>
          </div>
        ) : filteredItems.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-64 text-slate-500 dark:text-slate-400">
            <Receipt className="h-16 w-16 mb-4 opacity-50" />
            <p className="text-lg font-medium">Aucune facture trouvée</p>
            <p className="text-sm">Créez votre première facture</p>
          </div>
        ) : viewMode === 'grid' ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {filteredItems.map((inv) => {
              const statusConfig = getStatusConfig(inv.status);
              const StatusIcon = statusConfig.icon;
              
              return (
                <div
                  key={inv.id}
                  className="group relative p-5 rounded-xl border-2 border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 hover:shadow-xl hover:border-amber-300 dark:hover:border-amber-700 transition-all"
                >
                  {/* Status Badge */}
                  <div className="absolute top-3 right-3 flex flex-col items-end gap-2">
                    <div className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${statusConfig.color}`}>
                      <StatusIcon className="h-3 w-3" />
                      {statusConfig.label}
                    </div>
                    {inv.percentage < 100 && (
                      <div className="bg-orange-100 dark:bg-orange-950/30 text-orange-700 dark:text-orange-300 px-2 py-1 rounded-full text-xs font-bold">
                        ⚠ Partiel
                      </div>
                    )}
                  </div>

                  {/* Icon */}
                  <div className="w-12 h-12 bg-gradient-to-br from-amber-500 to-orange-600 rounded-xl flex items-center justify-center mb-3 shadow-md">
                    <Receipt className="h-6 w-6 text-white" />
                  </div>

                  {/* Info */}
                  <h3 className="font-bold text-slate-900 dark:text-slate-100 mb-1">
                    {inv.invoice_number}
                  </h3>
                  <div className="text-sm text-slate-600 dark:text-slate-400 space-y-1 mb-3">
                    <div className="flex items-center gap-1">
                      <User className="h-3 w-3" />
                      {inv.customer_name || 'Client inconnu'}
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-1">
                        <span className="font-bold text-amber-600">{inv.total.toFixed(2)} €</span>
                      </div>
                      <div className="text-3xl font-black text-orange-500">
                        {inv.percentage}%
                      </div>
                    </div>
                    {inv.payment_label && (
                      <div className="text-xs italic text-slate-500">
                        {inv.payment_label}
                      </div>
                    )}
                    <div className="flex items-center gap-1">
                      <Calendar className="h-3 w-3" />
                      {fmtDate(inv.issue_date)}
                    </div>
                    {inv.due_date && (
                      <div className="text-xs text-slate-500">
                        Échéance: {fmtDate(inv.due_date)}
                      </div>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="flex flex-col gap-2 pt-3 border-t border-slate-200 dark:border-slate-700">
                    <div className="flex items-center gap-1">
                      <Button size="sm" variant="ghost" onClick={() => navigate(`/invoices/${inv.id}`)} className="flex-1 text-blue-600" title="Consulter">
                        <Eye className="h-4 w-4" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => handleDownload(inv)} className="flex-1" title="Télécharger">
                        <Download className="h-4 w-4" />
                      </Button>
                      {inv.status === 'draft' && (
                        <Button size="sm" variant="ghost" onClick={() => handleSend(inv)} className="flex-1 text-green-600" title="Envoyer">
                          <Send className="h-4 w-4" />
                        </Button>
                      )}
                      {(inv.status === 'sent' || inv.status === 'overdue') && (
                        <Button size="sm" variant="ghost" onClick={() => handleMarkPaid(inv)} className="flex-1 text-green-600" title="Marquer payée">
                          <CheckCircle2 className="h-4 w-4" />
                        </Button>
                      )}
                      <Button size="sm" variant="ghost" onClick={() => handleDelete(inv)} className="text-red-600" title="Supprimer">
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                    {inv.status !== 'cancelled' && inv.status !== 'paid' && (
                      <div className="flex gap-1">
                        <Button size="sm" variant="outline" onClick={() => handleCancel(inv)} className="flex-1 text-xs text-red-600 hover:bg-red-50 dark:hover:bg-red-950/30" title="Annuler la facture">
                          <Ban className="h-3 w-3 mr-1" />
                          Annuler
                        </Button>
                        {inv.percentage < 100 && (
                          <Button size="sm" variant="outline" onClick={() => handleInitNext(inv)} className="flex-1 text-xs text-green-600 hover:bg-green-50 dark:hover:bg-green-950/30" title="Créer facture suivante">
                            <ArrowRight className="h-3 w-3 mr-1" />
                            Suivante
                          </Button>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="divide-y rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 overflow-hidden">
            {filteredItems.map((inv) => {
              const statusConfig = getStatusConfig(inv.status);
              const StatusIcon = statusConfig.icon;
              
              return (
                <div
                  key={inv.id}
                  className="flex items-center gap-4 p-4 hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors"
                >
                  <div className="w-10 h-10 bg-gradient-to-br from-amber-500 to-orange-600 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Receipt className="h-5 w-5 text-white" />
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-bold text-slate-900 dark:text-slate-100">
                        {inv.invoice_number}
                      </h3>
                      <span className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${statusConfig.color}`}>
                        <StatusIcon className="h-3 w-3" />
                        {statusConfig.label}
                      </span>
                      {inv.percentage < 100 && (
                        <span className="bg-orange-100 dark:bg-orange-950/30 text-orange-700 dark:text-orange-300 px-2 py-0.5 rounded-full text-xs font-bold">
                          ⚠ Partiel
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-slate-500 dark:text-slate-400 flex items-center gap-4">
                      <span>{inv.customer_name || 'Client inconnu'}</span>
                      <span>•</span>
                      <span className="font-bold text-amber-600">{inv.total.toFixed(2)} €</span>
                      <span>•</span>
                      <span className="text-lg font-black text-orange-500">{inv.percentage}%</span>
                      {inv.payment_label && (
                        <>
                          <span>•</span>
                          <span className="italic">{inv.payment_label}</span>
                        </>
                      )}
                      <span>•</span>
                      <span>{fmtDate(inv.issue_date)}</span>
                      {inv.due_date && (
                        <>
                          <span>•</span>
                          <span>Échéance: {fmtDate(inv.due_date)}</span>
                        </>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-1 flex-shrink-0">
                    <Button size="sm" variant="ghost" onClick={() => navigate(`/invoices/${inv.id}`)} className="text-blue-600" title="Consulter">
                      <Eye className="h-4 w-4" />
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => handleDownload(inv)} title="Télécharger">
                      <Download className="h-4 w-4" />
                    </Button>
                    {inv.status === 'draft' && (
                      <Button size="sm" variant="ghost" onClick={() => handleSend(inv)} className="text-green-600" title="Envoyer">
                        <Send className="h-4 w-4" />
                      </Button>
                    )}
                    {(inv.status === 'sent' || inv.status === 'overdue') && (
                      <Button size="sm" variant="ghost" onClick={() => handleMarkPaid(inv)} className="text-green-600" title="Marquer payée">
                        <CheckCircle2 className="h-4 w-4" />
                      </Button>
                    )}
                    {inv.status !== 'cancelled' && inv.status !== 'paid' && (
                      <Button size="sm" variant="ghost" onClick={() => handleCancel(inv)} className="text-red-600" title="Annuler">
                        <Ban className="h-4 w-4" />
                      </Button>
                    )}
                    {inv.percentage < 100 && inv.status !== 'cancelled' && inv.status !== 'paid' && (
                      <Button size="sm" variant="ghost" onClick={() => handleInitNext(inv)} className="text-green-600" title="Facture suivante">
                        <ArrowRight className="h-4 w-4" />
                      </Button>
                    )}
                    <Button size="sm" variant="ghost" onClick={() => handleDelete(inv)} className="text-red-600" title="Supprimer">
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Create Modal */}
      <CreateInvoiceModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSuccess={() => void load()}
      />

      {/* Init Next Invoice Modal */}
      {selectedInvoiceForNext && (
        <InitNextInvoiceModal
          isOpen={showInitNextModal}
          onClose={() => {
            setShowInitNextModal(false);
            setSelectedInvoiceForNext(null);
          }}
          onSuccess={() => void load()}
          quoteId={selectedInvoiceForNext.quote_id}
          currentInvoiceNumber={selectedInvoiceForNext.invoice_number}
        />
      )}

      {/* Cancel Invoice Modal */}
      {selectedInvoiceForCancel && (
        <CancelInvoiceModal
          isOpen={showCancelModal}
          onClose={() => {
            setShowCancelModal(false);
            setSelectedInvoiceForCancel(null);
          }}
          onSuccess={() => void load()}
          invoiceId={selectedInvoiceForCancel.id}
          invoiceNumber={selectedInvoiceForCancel.invoice_number}
        />
      )}
    </div>
  );
};

export default InvoiceList;
